import Swiper from 'swiper';
import {
	A11y,
	Autoplay,
	FreeMode,
	Keyboard,
	Navigation,
	Pagination,
} from 'swiper/modules';

import { effectValues, normalizeSettings } from './config.js';

const instances = new WeakMap();
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function parseSettings(root) {
	try {
		return JSON.parse(root.dataset.settings || '{}');
	} catch (error) {
		return {};
	}
}

function applyEffect(swiper, settings) {
	if (!swiper || !swiper.slides) {
		return;
	}

	swiper.slides.forEach((slide) => {
		const inner = slide.querySelector('.fd-review-slider__slide-inner');
		if (!inner) {
			return;
		}

		const values = effectValues(settings.effect, slide.progress, reducedMotionQuery.matches);
		inner.style.opacity = String(values.opacity);
		inner.style.transform = values.transform;
	});
}

function initializeEntranceAnimation(root, cleanupCallbacks) {
	if (!root.dataset.animation || reducedMotionQuery.matches) {
		root.classList.add('is-visible');
		return;
	}

	root.classList.add(`fd-review-slider--animation-${root.dataset.animation}`);

	if (!('IntersectionObserver' in window)) {
		root.classList.add('is-visible');
		return;
	}

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				entry.target.classList.add('is-visible');
				observer.unobserve(entry.target);
			}
		});
	}, { threshold: 0.12 });

	observer.observe(root);
	cleanupCallbacks.push(() => observer.disconnect());
}

function configureAutoplay(root, swiper, settings, cleanupCallbacks) {
	const button = root.querySelector('.fd-review-slider__autoplay');
	if (!button) {
		return;
	}

	if (!settings.autoplay || !swiper.autoplay) {
		button.hidden = true;
		return;
	}

	let userPaused = false;
	let temporarilyPaused = false;

	const updateButton = () => {
		const paused = userPaused || temporarilyPaused;
		button.setAttribute('aria-pressed', paused ? 'true' : 'false');
		button.setAttribute(
			'aria-label',
			paused ? 'Automatischen Wechsel starten' : 'Automatischen Wechsel pausieren',
		);
		const icon = button.querySelector('.fd-review-slider__autoplay-icon');
		if (icon) {
			icon.textContent = paused ? '▶' : 'Ⅱ';
		}
	};

	const stopTemporarily = () => {
		if (!userPaused) {
			temporarilyPaused = true;
			swiper.autoplay.stop();
			updateButton();
		}
	};

	const resumeTemporaryPause = (event) => {
		if (event.type === 'focusout' && root.contains(event.relatedTarget)) {
			return;
		}
		if (!userPaused) {
			temporarilyPaused = false;
			swiper.autoplay.start();
			updateButton();
		}
	};

	const toggleAutoplay = () => {
		userPaused = !userPaused;
		if (userPaused) {
			temporarilyPaused = false;
			swiper.autoplay.stop();
		} else {
			temporarilyPaused = root.matches(':hover') || root.contains(document.activeElement);
			if (temporarilyPaused) {
				swiper.autoplay.stop();
			} else {
				swiper.autoplay.start();
			}
		}
		updateButton();
	};

	button.addEventListener('click', toggleAutoplay);
	root.addEventListener('mouseenter', stopTemporarily);
	root.addEventListener('mouseleave', resumeTemporaryPause);
	root.addEventListener('focusin', stopTemporarily);
	root.addEventListener('focusout', resumeTemporaryPause);
	updateButton();

	cleanupCallbacks.push(() => {
		button.removeEventListener('click', toggleAutoplay);
		root.removeEventListener('mouseenter', stopTemporarily);
		root.removeEventListener('mouseleave', resumeTemporaryPause);
		root.removeEventListener('focusin', stopTemporarily);
		root.removeEventListener('focusout', resumeTemporaryPause);
	});
}

function initializeSlider(root) {
	const existing = instances.get(root);
	if (existing) {
		existing.cleanup.forEach((callback) => callback());
		existing.swiper.destroy(true, true);
		instances.delete(root);
	}

	const viewport = root.querySelector('.fd-review-slider__viewport');
	if (!viewport) {
		return;
	}

	const settings = normalizeSettings(parseSettings(root), reducedMotionQuery.matches);
	const previous = root.querySelector('.fd-review-slider__arrow--previous');
	const next = root.querySelector('.fd-review-slider__arrow--next');
	const pagination = root.querySelector('.fd-review-slider__pagination');
	const cleanupCallbacks = [];

	root.dir = settings.rtl ? 'rtl' : 'ltr';

	const swiper = new Swiper(viewport, {
		modules: [A11y, Autoplay, FreeMode, Keyboard, Navigation, Pagination],
		watchSlidesProgress: true,
		observer: true,
		observeParents: true,
		loop: settings.loop,
		centeredSlides: settings.cellAlign === 'center',
		autoHeight: settings.adaptiveHeight,
		freeMode: settings.freeMode ? { enabled: true, sticky: false } : false,
		keyboard: { enabled: true, onlyInViewport: true },
		breakpoints: settings.breakpoints,
		autoplay: settings.autoplay
			? {
				delay: settings.autoplayDelay,
				disableOnInteraction: false,
				pauseOnMouseEnter: false,
			}
			: false,
		navigation: settings.arrows && previous && next
			? { prevEl: previous, nextEl: next }
			: false,
		pagination: settings.dots && pagination
			? {
				el: pagination,
				clickable: true,
				renderBullet(index, className) {
					return `<button type="button" class="${className}" aria-label="Rezension ${index + 1} anzeigen"></button>`;
				},
			}
			: false,
		a11y: {
			enabled: true,
			containerMessage: root.getAttribute('aria-label') || 'Kundenstimmen',
			prevSlideMessage: 'Vorherige Rezension',
			nextSlideMessage: 'Naechste Rezension',
			firstSlideMessage: 'Dies ist die erste Rezension',
			lastSlideMessage: 'Dies ist die letzte Rezension',
			paginationBulletMessage: 'Rezension {{index}} anzeigen',
		},
		on: {
			init(instance) {
				applyEffect(instance, settings);
			},
			progress(instance) {
				applyEffect(instance, settings);
			},
			setTransition(instance, duration) {
				instance.slides.forEach((slide) => {
					const inner = slide.querySelector('.fd-review-slider__slide-inner');
					if (inner) {
						inner.style.transitionDuration = `${duration}ms`;
					}
				});
			},
			touchStart(instance) {
				if (settings.dragScale) {
					instance.el.classList.add('is-dragging');
				}
			},
			touchEnd(instance) {
				instance.el.classList.remove('is-dragging');
			},
		},
	});

	initializeEntranceAnimation(root, cleanupCallbacks);
	configureAutoplay(root, swiper, settings, cleanupCallbacks);
	instances.set(root, { swiper, cleanup: cleanupCallbacks });
}

function initializeWithin(scope = document) {
	const roots = scope.matches?.('.fd-review-slider')
		? [scope]
		: Array.from(scope.querySelectorAll?.('.fd-review-slider') || []);

	roots.forEach(initializeSlider);
}

function initializeElementor() {
	if (!window.elementorFrontend?.hooks) {
		return;
	}

	window.elementorFrontend.hooks.addAction(
		'frontend/element_ready/fd-review-slider.default',
		($scope) => initializeWithin($scope?.[0] || $scope),
	);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
} else {
	initializeWithin(document);
}

if (window.elementorFrontend?.hooks) {
	initializeElementor();
} else {
	window.addEventListener('elementor/frontend/init', initializeElementor, { once: true });
}
